import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { generateClient } from 'aws-amplify/api';
import type { Schema } from '../../amplify/data/resource';
import PageHeader from '@/components/layout/PageHeader';
import Button from '@/components/ui/Button';

const client = generateClient<Schema>();

export default function AdminPanel() {
  const [inviteUrl, setInviteUrl] = useState('');
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  const { data: invites = [], refetch } = useQuery({
    queryKey: ['invite-tokens'],
    queryFn: async () => {
      const { data } = await client.models.InviteToken.list({ limit: 20 });
      return data ?? [];
    },
  });

  async function handleGenerateInvite() {
    setGenerating(true);
    setInviteUrl('');
    const result = await client.mutations.generateInviteLink({});
    if (result.data) {
      setInviteUrl(result.data);
      refetch();
    }
    setGenerating(false);
  }

  async function handleCopy() {
    await navigator.clipboard.writeText(inviteUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  const activeInvites = invites.filter(
    (i) => !i.usedBy && new Date(i.expiresAt) > new Date()
  );

  return (
    <div className="page page-admin">
      <PageHeader title="Admin" back />

      <section className="admin-section">
        <h2 className="section-title">Invite a family member</h2>
        <p className="admin-description">
          Generate a one-time invite link (valid 7 days). Share it directly with the person.
        </p>

        <Button
          variant="primary"
          onClick={handleGenerateInvite}
          loading={generating}
        >
          Generate invite link
        </Button>

        {inviteUrl && (
          <div className="invite-result">
            <p className="invite-url">{inviteUrl}</p>
            <Button size="sm" variant="secondary" onClick={handleCopy}>
              {copied ? 'Copied!' : 'Copy link'}
            </Button>
          </div>
        )}
      </section>

      <section className="admin-section">
        <h2 className="section-title">Active invites ({activeInvites.length})</h2>
        {activeInvites.length === 0 ? (
          <p className="admin-empty">No active invite links.</p>
        ) : (
          <ul className="admin-list">
            {activeInvites.map((invite) => (
              <li key={invite.id} className="admin-list-item">
                <span className="admin-token">{invite.token.slice(0, 12)}…</span>
                <span className="admin-expiry">
                  Expires {new Date(invite.expiresAt).toLocaleDateString()}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
